/**
 * OptionSet Dataverse usati dal portale acquisti.
 *
 * I valori sono **letti dai metadati** di produzione il 2026-09-04
 * (`EntityDefinitions(...)/Attributes(...)/PicklistAttributeMetadata`), non dedotti: due delle
 * tabelle coinvolte sono preesistenti e i loro OptionSet non seguono nessuna convenzione che
 * permetta di indovinarli.
 *
 * Sono duplicati rispetto al backend per necessita': Dataverse non espone le etichette al client,
 * e il backend restituisce il numero. Se un OptionSet cambia su Dataverse va cambiato anche qui —
 * al momento non c'e' un endpoint che li serva, ed e' un debito da tenere presente.
 */

/** phc_contrattofornitore.phc_statocontratto */
export const STATO_CONTRATTO = {
  100000000: "In definizione",
  100000001: "Attivo",
  100000002: "In rinnovo",
  100000003: "In scadenza",
  100000004: "Scaduto",
  100000005: "Dismesso",
  100000006: "Disdetto",
};
/** Lo scrive solo la registrazione dell'invio della disdetta (decisione 2026-10-30). */
export const CONTRATTO_DISDETTO = 100000006;
export const CONTRATTO_DISMESSO = 100000005;

/** Colore del badge per stato: solo estetica, nessuna logica ci si appoggia. */
export const STATO_CONTRATTO_BADGE = {
  100000000: "badge-gray",
  100000001: "badge-success",
  100000002: "badge-info",
  100000003: "badge-warning",
  100000004: "badge-danger",
  100000005: "badge-gray",
  100000006: "badge-danger",
};

/** phc_contrattofornitore.phc_formacomunicazione — forma richiesta dal contratto per la disdetta. */
export const FORMA_COMUNICAZIONE = {
  100000000: "PEC",
  100000001: "Raccomandata A/R",
  100000002: "PEC e raccomandata A/R",
};

/** phc_contrattofornitore.phc_tipochiusura */
export const TIPO_CHIUSURA = {
  100000000: "Disdetta (rinnovo tacito)",
  100000001: "Cessazione a termine",
  100000002: "Recesso anticipato",
  100000003: "Risoluzione consensuale",
};

/** I quattro modelli di comunicazione, come in procedure/Modelli_PEC_chiusura_contratti.md. */
export const MODELLI_CHIUSURA = {
  A: "A — Disdetta per evitare il rinnovo tacito",
  B: "B — Cessazione alla scadenza (contratto a termine)",
  C: "C — Recesso anticipato con preavviso",
  D: "D — Risoluzione consensuale (termine già passato)",
};

/** phc_contrattofornitore.phc_tipoimporto */
export const TIPO_IMPORTO = {
  100000000: "Canone fisso",
  100000001: "A consumo",
  100000002: "Misto",
};

/** phc_contrattofornitore.phc_basepagamento */
export const BASE_PAGAMENTO = {
  100000000: "Data Fattura",
  100000001: "Fine Mese",
  100000002: "Fine Mese + N giorni",
};

/**
 * phc_contrattofornitore.phc_modalitafatturazione — come si fattura il contratto (rate, SAL, ...).
 * Diversa da `BASE_PAGAMENTO`, che dice quando si paga la singola fattura. Stessi valori di
 * scripts/setup-acq-fatturazione-schema.mjs.
 */
export const MODALITA_FATTURAZIONE = {
  100000000: "Unica soluzione",
  100000001: "Mensile",
  100000002: "Bimestrale",
  100000003: "Trimestrale",
  100000004: "Semestrale",
  100000005: "Annuale",
  100000006: "A SAL (stato avanzamento lavori)",
  100000007: "A consumo / a intervento",
  100000008: "Altro criterio",
};
/** Modalita' che senza dettaglio non dicono abbastanza: la pagina lo chiede esplicitamente. */
export const FATTURAZIONE_CON_DETTAGLIO = [100000006, 100000008];

/**
 * phc_contrattofornitore.phc_modalitarinnovo
 * `100000002` e' il caso che rende necessaria `phc_datadisdettaentro`, e quindi il job disdette.
 */
export const MODALITA_RINNOVO = {
  100000000: "A termine",
  100000001: "Rinnovo automatico",
  100000002: "Rinnovo tacito con disdetta",
};
export const RINNOVO_TACITO_CON_DISDETTA = 100000002;

/**
 * phc_contrattofornitore.phc_ambito. Le prime tre sono le stesse di `phc_fornitore.phc_tipofornitore`
 * (la riconciliazione di ADR-0004 era un confronto, non una conversione); «Servizi generali» e
 * «Consulenza» e «Appalto» aggiunte il 2026-10-14 solo qui — `phc_tipofornitore` e' dismessa e non si estende.
 * «Entrambi» resta per i contratti misti manutenzione + utenze.
 */
export const AMBITO = {
  100000000: "Manutenzione",
  100000001: "Utenze",
  100000002: "Entrambi (manutenzione e utenze)",
  100000003: "Servizi generali",
  100000004: "Consulenza",
  100000005: "Appalto",
};

/** phc_contrattofornitore.phc_indicizzazione */
export const INDICIZZAZIONE = {
  100000000: "Nessuna",
  100000001: "ISTAT FOI",
  100000002: "ISTAT NIC",
  100000003: "Altra formula",
  100000004: "Bellica",
};

/** phc_acqcapitolato.phc_stato */
export const STATO_CAPITOLATO = {
  100000000: "Bozza",
  100000001: "Attivo",
  100000002: "Archiviato",
};
export const CAPITOLATO_BOZZA = 100000000;
export const CAPITOLATO_ATTIVO = 100000001;
export const CAPITOLATO_ARCHIVIATO = 100000002;

/** phc_acqcapitolato.phc_tipo — vuoto = Semplice (ADR-0007) */
export const TIPO_CAPITOLATO = {
  100000000: "Semplice",
  100000001: "Master",
};
export const CAPITOLATO_SEMPLICE = 100000000;
export const CAPITOLATO_MASTER = 100000001;

export const STATO_CAPITOLATO_BADGE = {
  100000000: "badge-gray",
  100000001: "badge-success",
  100000002: "badge-warning",
};

/** phc_acqcapitolatovoce.phc_sezione */
export const SEZIONE_VOCE = {
  100000000: "Tecnica",
  100000001: "Economica",
  100000002: "SLA",
  100000003: "Qualità",
  100000004: "Contrattuale",
};

/** phc_acqcapitolatovoce.phc_tiporisposta */
export const TIPO_RISPOSTA = {
  100000000: "Numerico",
  100000001: "Importo",
  100000002: "Percentuale",
  100000003: "Sì / No",
  100000004: "Testo",
  100000005: "Data",
};

/** phc_acqcapitolatovoce.phc_formula */
export const FORMULA = {
  100000000: "Prezzo più basso proporzionale",
  100000001: "Lineare min-max (più alto è meglio)",
  100000002: "Lineare max-min (più basso è meglio)",
  100000003: "Booleano",
  100000004: "Manuale",
};

/** Scala su cui si punteggia una voce, se non specificata. Default applicativo del backend. */
export const PUNTEGGIO_MASSIMO_DEFAULT = 10;

/** phc_fornitore.phc_livelloriservatezza — ADR-0002. */
export const LIVELLO_RISERVATEZZA = {
  100000000: "Condiviso",
  100000001: "In trattativa",
  100000002: "Riservato",
  100000003: "Strettamente riservato",
};
export const LIVELLO_CONDIVISO = 100000000;
export const LIVELLO_IN_TRATTATIVA = 100000001;

export const LIVELLO_BADGE = {
  100000000: "badge-gray",
  100000001: "badge-warning",
  100000002: "badge-info",
  100000003: "badge-danger",
};

/** phc_acqgara.phc_stato — sprint 04. */
export const STATO_GARA = {
  100000000: "Bozza",
  100000001: "Inviata",
  100000002: "In valutazione",
  100000003: "Aggiudicata",
  100000004: "Annullata",
};
export const GARA_BOZZA = 100000000;
export const GARA_INVIATA = 100000001;
export const GARA_IN_VALUTAZIONE = 100000002;
export const GARA_AGGIUDICATA = 100000003;
export const GARA_ANNULLATA = 100000004;

export const STATO_GARA_BADGE = {
  100000000: "badge-gray",
  100000001: "badge-info",
  100000002: "badge-warning",
  100000003: "badge-success",
  100000004: "badge-danger",
};

/** phc_acqgara.phc_origine */
export const ORIGINE_GARA = {
  100000000: "Manuale",
  100000001: "Promemoria disdetta",
};

/** phc_acqgarafornitore.phc_stato */
export const STATO_INVITO = {
  100000000: "Da invitare",
  100000001: "Invitato",
  100000002: "Invio fallito",
  100000003: "Ha risposto",
  100000004: "Ha declinato",
};
export const INVITO_DA_INVITARE = 100000000;
export const INVITO_INVITATO = 100000001;
export const INVITO_FALLITO = 100000002;
export const INVITO_HA_RISPOSTO = 100000003;

export const STATO_INVITO_BADGE = {
  100000000: "badge-gray",
  100000001: "badge-info",
  100000002: "badge-danger",
  100000003: "badge-success",
  100000004: "badge-warning",
};

/** phc_acqofferta.phc_stato */
export const STATO_OFFERTA = {
  100000000: "In compilazione",
  100000001: "Completa",
  100000002: "Superata",
  100000003: "Ritirata",
};
export const OFFERTA_IN_COMPILAZIONE = 100000000;
export const OFFERTA_COMPLETA = 100000001;
export const OFFERTA_SUPERATA = 100000002;
export const OFFERTA_RITIRATA = 100000003;

export const STATO_OFFERTA_BADGE = {
  100000000: "badge-gray",
  100000001: "badge-success",
  100000002: "badge-warning",
  100000003: "badge-danger",
};

/** phc_acqoffertavoce.phc_conformita — giudizio manuale, vuoto = decidono le soglie. */
export const CONFORMITA = {
  100000000: "Conforme",
  100000001: "Non conforme",
};

/** Valori di phc_tiporisposta usati per scegliere il campo di input. */
export const RISPOSTA_SI_NO = 100000003;
export const RISPOSTA_TESTO = 100000004;
export const RISPOSTA_DATA = 100000005;
export const FORMULA_MANUALE = 100000004;

/** phc_acqanalisi.phc_stato — sprint 05. */
export const STATO_ANALISI = {
  100000000: "Bozza",
  100000001: "Approvata",
  100000002: "Superata",
};
export const ANALISI_BOZZA = 100000000;
export const ANALISI_APPROVATA = 100000001;

export const STATO_ANALISI_BADGE = {
  100000000: "badge-warning",
  100000001: "badge-success",
  100000002: "badge-gray",
};

/** `{value, label}` per una <select>, dall'oggetto `valore -> etichetta`. */
export function opzioni(mappa) {
  return Object.entries(mappa).map(([value, label]) => ({
    value: Number(value),
    label,
  }));
}
